// Pure rules of the guided support intake (no React). The validation here mirrors the backend
// (lud-backend/utils/copilotValidation.js) so the chat can answer in a friendly way before anything
// is sent; the backend still validates everything again.

export const STEPS = Object.freeze({ CATEGORY: "category", RESTAURANT: "restaurant", MESSAGE: "message", NAME: "name", EMAIL: "email", PHONE: "phone", REVIEW: "review", EDIT: "edit" });
export const MESSAGE_MAX = 5000;
export const NAME_MIN = 2;
export const NAME_MAX = 100;

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F​-‏‪-‮⁦-⁩﻿]/g;
const HTML_TAGS = /<\/?[a-zA-Z!][^>]*>/g;
const EMAIL_LOCAL = /^[^\s@<>()[\]\\,;:"]+$/;
const EMAIL_LABEL = /^[\p{L}\p{N}]([\p{L}\p{N}-]*[\p{L}\p{N}])?$/u;
const EMAIL_TLD = /^\p{L}{2,}$|^xn--[a-z0-9-]{2,}$/iu;
const PHONE_CHARS = /^\+?[0-9][0-9\s\-().]{3,24}[0-9]$/;
const isSaudiNational = (digits) => /^(5\d{8}|1[1-9]\d{7})$/.test(digits);

/** Trimmed, lower-case address, or null when it is malformed. Syntax only: never proof of ownership. */
export function normalizeEmail(value) {
  if (typeof value !== "string") return null;
  const email = value.normalize("NFC").replace(CONTROL_CHARS, "").trim().toLowerCase();
  if (!email || email.length > 254) return null;
  const at = email.lastIndexOf("@");
  if (at < 1 || at !== email.indexOf("@")) return null;
  const local = email.slice(0, at);
  const labels = email.slice(at + 1).split(".");
  if (local.length > 64 || !EMAIL_LOCAL.test(local) || local.startsWith(".") || local.endsWith(".") || local.includes("..")) return null;
  if (labels.length < 2 || labels.some((label) => !label || label.length > 63 || !EMAIL_LABEL.test(label))) return null;
  return EMAIL_TLD.test(labels[labels.length - 1]) ? email : null;
}

function normalizeInternational(digits) {
  if (!/^[1-9]\d{7,14}$/.test(digits)) return null;
  if (digits.startsWith("966")) {
    const national = digits.slice(3).replace(/^0/, "");
    return isSaudiNational(national) ? `+966${national}` : null;
  }
  return `+${digits}`;
}

/** E.164 ("+9665XXXXXXXX") for Saudi (05XXXXXXXX, 5XXXXXXXX, 966…, 00966…) and international (+ / 00) numbers, else null. */
export function normalizePhone(value) {
  if (typeof value !== "string") return null;
  const raw = value.normalize("NFC").replace(CONTROL_CHARS, "").trim();
  if (!PHONE_CHARS.test(raw)) return null;
  let digits = raw.replace(/\D/g, "");
  if (raw.startsWith("+")) return normalizeInternational(digits);
  if (digits.startsWith("00")) return normalizeInternational(digits.slice(2));
  if (digits.startsWith("966")) return normalizeInternational(digits);
  if (digits.startsWith("0")) digits = digits.slice(1);
  return isSaudiNational(digits) ? `+966${digits}` : null;
}

/** Cleaned name or null (2-100 characters, at least one letter). */
export function cleanName(value) {
  if (typeof value !== "string") return null;
  const name = value.normalize("NFC").replace(CONTROL_CHARS, "").replace(HTML_TAGS, "").replace(/\s+/g, " ").trim();
  return name.length >= NAME_MIN && name.length <= NAME_MAX && /\p{L}/u.test(name) ? name : null;
}

/** Cleaned description or null when empty. */
export function cleanMessage(value) {
  if (typeof value !== "string") return null;
  const text = value.normalize("NFC").replace(CONTROL_CHARS, "").replace(HTML_TAGS, "").trim();
  return text || null;
}

/** "Ahmed Ali" -> "Ahmed": the customer is addressed by their first name. */
export const firstName = (name) => String(name || "").trim().split(/\s+/)[0] || "";

/**
 * What the chat needs to ask, in order, for this visitor.
 *   mode "guest"  category, message, name, email, phone, review
 *   mode "user"   category, [restaurant when the account has several], message,
 *                 [phone when the account has no valid one], review
 * A category pre-filled from an unhelpful FAQ is not asked again (it stays editable).
 */
export function applicableSteps({ mode, categoryKnown, accountPhoneValid, needsRestaurant = false }) {
  const steps = [];
  if (!categoryKnown) steps.push(STEPS.CATEGORY);
  if (mode === "user" && needsRestaurant) steps.push(STEPS.RESTAURANT);
  steps.push(STEPS.MESSAGE);
  if (mode === "guest") steps.push(STEPS.NAME, STEPS.EMAIL);
  if (mode === "guest" || !accountPhoneValid) steps.push(STEPS.PHONE);
  steps.push(STEPS.REVIEW);
  return steps;
}

/** Steps the customer may jump back to from the review ("Edit details"). */
export function editableSteps({ mode, accountPhoneValid, needsRestaurant }) {
  return applicableSteps({ mode, categoryKnown: false, accountPhoneValid, needsRestaurant }).filter((step) => step !== STEPS.REVIEW);
}

export const EMPTY_INTAKE = Object.freeze({
  step: STEPS.CATEGORY,
  editing: false,
  category: "",
  restaurant: "",
  message: "",
  name: "",
  email: "",
  phone: "",
  input: "",
  faq: null, // { key, category (support category), question: {ar,en}, answer: {ar,en} }
  confirmCancel: false,
  status: "filling", // filling | submitting | done
  error: null,
  done: null, // { id, reference, name, hasAccess }
});

/** True when the customer has typed or chosen something worth keeping. */
export const hasProgress = (intake) => Boolean(intake && (intake.message || intake.name || intake.email || intake.phone || intake.input || intake.faq || (intake.step && intake.step !== STEPS.CATEGORY)));

/** Fields kept in the browser's session storage between page loads (never files). */
export const persistable = (intake) => ({
  step: intake.step,
  editing: intake.editing,
  category: intake.category,
  restaurant: intake.restaurant,
  message: intake.message,
  name: intake.name,
  email: intake.email,
  phone: intake.phone,
  input: intake.input,
  faq: intake.faq,
});
